import { useState, useEffect, useCallback } from 'react';
import {
  Settings,
  Building2,
  MapPin,
  Wrench,
  Bell,
  Plug,
  CheckCircle2,
  XCircle,
  Loader2,
  Save,
  AlertCircle,
  RefreshCw,
  Users,
  Key,
  ClipboardList,
  UserPlus,
  Trash2,
  Copy,
  Plus,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { apiFetch } from '@/lib/api';

interface FleetSettings {
  id: string;
  tenant_id: string;
  type: string;
  company_name: string;
  timezone: string;
  distance_unit: 'miles' | 'kilometers';
  gps_provider: string;
  gps_api_key: string;
  gps_polling_interval: number;
  maintenance_oil_change_interval: number;
  maintenance_tire_rotation_interval: number;
  maintenance_alert_days: number;
  maintenance_alert_miles: number;
  notifications_email: boolean;
  notifications_sms: boolean;
  integrations: {
    psa: { connected: boolean; last_tested?: string };
    books: { connected: boolean; last_tested?: string };
    bus: { connected: boolean; last_tested?: string };
  };
  created_at: string;
  updated_at: string;
}

const inputCls = 'w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500';
const labelCls = 'block text-sm font-medium text-muted-foreground mb-1.5';
const selectCls = 'w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500 appearance-none';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'America/Toronto', 'America/Vancouver', 'Europe/London', 'Europe/Berlin',
  'Asia/Tokyo', 'Australia/Sydney', 'UTC',
];

type SettingsForm = Omit<FleetSettings, 'id' | 'tenant_id' | 'type' | 'created_at' | 'updated_at' | 'integrations'>;

export default function AdminPage() {
  const { data: settings, isLoading, error: loadError, refetch } = useApi<FleetSettings>('/api/settings');

  const [form, setForm] = useState<SettingsForm | null>(null);
  const [original, setOriginal] = useState<SettingsForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [togglingNotif, setTogglingNotif] = useState<string | null>(null);
  const [testingIntegration, setTestingIntegration] = useState<string | null>(null);
  const [integrationResults, setIntegrationResults] = useState<Record<string, { connected: boolean; message: string }>>({});
  const [integrations, setIntegrations] = useState(settings?.integrations);
  const [activeTab, setActiveTab] = useState<'general' | 'team' | 'api-keys' | 'audit'>('general');

  // Sync settings to form state
  useEffect(() => {
    if (settings) {
      const formData: SettingsForm = {
        company_name: settings.company_name,
        timezone: settings.timezone,
        distance_unit: settings.distance_unit,
        gps_provider: settings.gps_provider,
        gps_api_key: settings.gps_api_key,
        gps_polling_interval: settings.gps_polling_interval,
        maintenance_oil_change_interval: settings.maintenance_oil_change_interval,
        maintenance_tire_rotation_interval: settings.maintenance_tire_rotation_interval,
        maintenance_alert_days: settings.maintenance_alert_days,
        maintenance_alert_miles: settings.maintenance_alert_miles,
        notifications_email: settings.notifications_email,
        notifications_sms: settings.notifications_sms,
      };
      setForm(formData);
      setOriginal(formData);
      setIntegrations(settings.integrations);
    }
  }, [settings]);

  const isDirty = form && original && JSON.stringify(form) !== JSON.stringify(original);

  const updateField = useCallback(<K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) => {
    setForm(f => f ? { ...f, [key]: value } : f);
    setSaveMsg(null);
  }, []);

  // Save all form changes
  const handleSave = async () => {
    if (!form || !isDirty) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const changes: Record<string, unknown> = {};
      const keys: (keyof SettingsForm)[] = [
        'company_name', 'timezone', 'distance_unit', 'gps_provider', 'gps_api_key',
        'gps_polling_interval', 'maintenance_oil_change_interval', 'maintenance_tire_rotation_interval',
        'maintenance_alert_days', 'maintenance_alert_miles', 'notifications_email', 'notifications_sms',
      ];
      for (const key of keys) {
        if (form[key] !== original![key]) {
          changes[key] = form[key];
        }
      }
      await apiFetch('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify(changes),
      });
      setSaveMsg({ type: 'success', text: 'Settings saved successfully' });
      setOriginal({ ...form });
    } catch (err) {
      setSaveMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  // Toggle notification immediately
  const toggleNotification = async (field: 'notifications_email' | 'notifications_sms') => {
    if (!form) return;
    const newValue = !form[field];
    setTogglingNotif(field);
    try {
      await apiFetch('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify({ [field]: newValue }),
      });
      setForm(f => f ? { ...f, [field]: newValue } : f);
      setOriginal(o => o ? { ...o, [field]: newValue } : o);
    } catch {
      // revert on failure
    } finally {
      setTogglingNotif(null);
    }
  };

  // Test integration connection
  const testIntegration = async (product: string) => {
    setTestingIntegration(product);
    try {
      const result = await apiFetch<{ product: string; connected: boolean; message: string }>(
        `/api/settings/integrations/${product}/test`,
        { method: 'POST' },
      );
      setIntegrationResults(r => ({ ...r, [product]: { connected: result.connected, message: result.message } }));
      setIntegrations((prev: FleetSettings['integrations'] | undefined) => prev ? {
        ...prev,
        [product]: { connected: result.connected, last_tested: new Date().toISOString() },
      } : prev);
    } catch (err) {
      setIntegrationResults(r => ({ ...r, [product]: { connected: false, message: err instanceof Error ? err.message : 'Test failed' } }));
    } finally {
      setTestingIntegration(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="rounded-xl border border-rose-800 bg-rose-900/20 p-6 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-rose-400 mb-3" />
        <p className="text-sm text-rose-300">{loadError.message}</p>
        <button onClick={refetch} className="mt-3 text-sm text-emerald-400 hover:text-emerald-300">
          Try again
        </button>
      </div>
    );
  }

  if (!form) return null;

  const integrationList = [
    { key: 'psa', name: 'The One PSA', description: 'Sync vehicles with client assets and service tickets' },
    { key: 'books', name: 'The One Books', description: 'Push fuel and maintenance costs to accounting' },
    { key: 'bus', name: 'The One Bus', description: 'Publish fleet events to the activity bus' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="h-6 w-6 text-emerald-500" />
          <h1 className="text-2xl font-bold text-foreground">Fleet Settings</h1>
        </div>
        <div className="flex items-center gap-3">
          {saveMsg && (
            <span className={`text-sm ${saveMsg.type === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>
              {saveMsg.text}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-foreground transition hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-border">
        {(['general', 'team', 'api-keys', 'audit'] as const).map(tab => {
          const Icon = { general: Settings, team: Users, 'api-keys': Key, audit: ClipboardList }[tab];
          const label = { general: 'General', team: 'Team', 'api-keys': 'API Keys', audit: 'Audit Log' }[tab];
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      {activeTab === 'general' && <>
      {/* General Settings */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <Building2 className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-foreground">General</h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <label className={labelCls}>Company Name</label>
            <input
              type="text"
              className={inputCls}
              value={form.company_name}
              onChange={e => updateField('company_name', e.target.value)}
              placeholder="Enter company name"
            />
          </div>
          <div>
            <label className={labelCls}>Timezone</label>
            <select
              className={selectCls}
              value={form.timezone}
              onChange={e => updateField('timezone', e.target.value)}
            >
              {TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Distance Unit</label>
            <select
              className={selectCls}
              value={form.distance_unit}
              onChange={e => updateField('distance_unit', e.target.value as 'miles' | 'kilometers')}
            >
              <option value="miles">Miles</option>
              <option value="kilometers">Kilometers</option>
            </select>
          </div>
        </div>
      </div>

      {/* GPS Provider */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <MapPin className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-foreground">GPS Provider</h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <label className={labelCls}>Provider</label>
            <input
              type="text"
              className={inputCls}
              value={form.gps_provider}
              onChange={e => updateField('gps_provider', e.target.value)}
              placeholder="e.g. Samsara, Geotab"
            />
          </div>
          <div>
            <label className={labelCls}>API Key</label>
            <input
              type="text"
              className={inputCls + ' font-mono'}
              value={form.gps_api_key}
              onChange={e => updateField('gps_api_key', e.target.value)}
              placeholder="Enter API key"
            />
          </div>
          <div>
            <label className={labelCls}>Polling Interval (seconds)</label>
            <input
              type="number"
              className={inputCls}
              value={form.gps_polling_interval}
              onChange={e => updateField('gps_polling_interval', parseInt(e.target.value, 10) || 30)}
              min={5}
              max={3600}
            />
          </div>
        </div>
      </div>

      {/* Maintenance */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <Wrench className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-foreground">Maintenance</h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label className={labelCls}>Default Oil Change Interval ({form.distance_unit})</label>
            <input
              type="number"
              className={inputCls}
              value={form.maintenance_oil_change_interval}
              onChange={e => updateField('maintenance_oil_change_interval', parseInt(e.target.value, 10) || 5000)}
              min={100}
              max={100000}
            />
          </div>
          <div>
            <label className={labelCls}>Default Tire Rotation Interval ({form.distance_unit})</label>
            <input
              type="number"
              className={inputCls}
              value={form.maintenance_tire_rotation_interval}
              onChange={e => updateField('maintenance_tire_rotation_interval', parseInt(e.target.value, 10) || 7500)}
              min={100}
              max={100000}
            />
          </div>
          <div>
            <label className={labelCls}>Alert Threshold (days before due)</label>
            <input
              type="number"
              className={inputCls}
              value={form.maintenance_alert_days}
              onChange={e => updateField('maintenance_alert_days', parseInt(e.target.value, 10) || 14)}
              min={1}
              max={365}
            />
          </div>
          <div>
            <label className={labelCls}>Alert Threshold ({form.distance_unit} before due)</label>
            <input
              type="number"
              className={inputCls}
              value={form.maintenance_alert_miles}
              onChange={e => updateField('maintenance_alert_miles', parseInt(e.target.value, 10) || 500)}
              min={50}
              max={10000}
            />
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <Bell className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
        </div>
        <div className="space-y-4">
          {([
            { field: 'notifications_email' as const, label: 'Email Alerts', desc: 'Receive maintenance reminders and critical alerts via email' },
            { field: 'notifications_sms' as const, label: 'SMS Alerts', desc: 'Receive urgent notifications via text message' },
          ]).map(({ field, label, desc }) => {
            const on = form[field];
            const toggling = togglingNotif === field;
            return (
              <div key={field} className="flex items-center justify-between rounded-lg border border-border bg-muted/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
                <button
                  onClick={() => toggleNotification(field)}
                  disabled={toggling}
                  className="relative"
                >
                  {toggling ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  ) : (
                    <div className={`h-6 w-11 rounded-full p-0.5 transition-colors ${on ? 'bg-emerald-600' : 'bg-muted'}`}>
                      <div className={`h-5 w-5 rounded-full transition-transform ${on ? 'bg-white translate-x-5' : 'bg-muted-foreground'}`} />
                    </div>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Integrations */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <Plug className="h-5 w-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-foreground">Integrations</h2>
        </div>
        <div className="space-y-3">
          {integrationList.map(({ key, name, description }) => {
            const status = integrations?.[key as keyof typeof integrations];
            const testing = testingIntegration === key;
            const result = integrationResults[key];
            return (
              <div
                key={key}
                className="rounded-lg border border-border bg-muted/50 px-4 py-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{name}</p>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {status?.connected ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                          <span className="text-xs font-medium text-emerald-400">Connected</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground">Not Connected</span>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => testIntegration(key)}
                      disabled={testing}
                      className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-40"
                    >
                      {testing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      Test
                    </button>
                  </div>
                </div>
                {result && (
                  <div className={`mt-2 rounded px-3 py-1.5 text-xs ${result.connected ? 'bg-emerald-900/30 text-emerald-300' : 'bg-rose-900/30 text-rose-300'}`}>
                    {result.message}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </>}

      {activeTab === 'team' && <FleetTeamTab />}
      {activeTab === 'api-keys' && <FleetApiKeysTab />}
      {activeTab === 'audit' && <FleetAuditTab />}
    </div>
  );
}

// ─── Team Tab ──────────────────────────────────────────────────────────────────

interface FleetMember { id: string; name: string; email: string; role: string; }

function FleetTeamTab() {
  const [members, setMembers] = useState<FleetMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'editor' | 'viewer'>('viewer');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const d = await apiFetch<{ data: FleetMember[] }>('/api/team');
      setMembers(d.data ?? []);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviting(true); setInviteError(null);
    try {
      await apiFetch('/api/team/invite', { method: 'POST', body: JSON.stringify({ email: inviteEmail, role: inviteRole }) });
      setInviteEmail('');
      await load();
    } catch (e) { setInviteError(e instanceof Error ? e.message : 'Failed to invite'); }
    finally { setInviting(false); }
  }

  async function handleRoleChange(userId: string, role: string) {
    try {
      await apiFetch(`/api/team/${userId}/role`, { method: 'PATCH', body: JSON.stringify({ role }) });
      setMembers(prev => prev.map(m => m.id === userId ? { ...m, role } : m));
    } catch (e) { alert(e instanceof Error ? e.message : 'Failed to update'); }
  }

  async function handleRemove(userId: string, email: string) {
    if (!confirm(`Remove ${email}?`)) return;
    try {
      await apiFetch(`/api/team/${userId}`, { method: 'DELETE' });
      setMembers(prev => prev.filter(m => m.id !== userId));
    } catch (e) { alert(e instanceof Error ? e.message : 'Failed to remove'); }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="text-base font-semibold text-foreground mb-4">Invite Team Member</h3>
        <form onSubmit={handleInvite} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className={labelCls}>Email</label>
            <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="name@company.com" className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value as 'admin' | 'editor' | 'viewer')} className={selectCls}>
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button type="submit" disabled={inviting} className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-foreground transition hover:bg-emerald-500 disabled:opacity-40">
            <UserPlus className="h-4 w-4" />
            {inviting ? 'Inviting…' : 'Invite'}
          </button>
        </form>
        {inviteError && <p className="mt-2 text-sm text-rose-400">{inviteError}</p>}
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-foreground">Team Members</h3>
          <button onClick={load} className="text-muted-foreground hover:text-foreground"><RefreshCw className="h-4 w-4" /></button>
        </div>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 text-muted-foreground animate-spin" /></div>
          : error ? <p className="text-sm text-rose-400">{error}</p>
          : members.length === 0 ? <p className="text-sm text-muted-foreground">No team members yet.</p>
          : <div className="divide-y divide-border">
            {members.map(m => (
              <div key={m.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{m.name || m.email}</p>
                  <p className="text-xs text-muted-foreground">{m.email}</p>
                </div>
                <div className="flex items-center gap-3">
                  <select value={m.role} onChange={e => handleRoleChange(m.id, e.target.value)} className="rounded border border-border bg-muted px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500">
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button onClick={() => handleRemove(m.id, m.email)} className="text-muted-foreground hover:text-rose-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}

// ─── API Keys Tab ──────────────────────────────────────────────────────────────

interface FleetApiKey { id: string; name: string; key_prefix: string; scopes: string[]; last_used_at?: string; expires_at?: string; created_at: string; }
interface FleetApiKeyCreated extends FleetApiKey { key: string; warning: string; }

const FLEET_SCOPES = ['vehicles:read', 'vehicles:write', 'gps:read', 'maintenance:read', 'fuel:read', 'analytics:read'];

function FleetApiKeysTab() {
  const [keys, setKeys] = useState<FleetApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<FleetApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createScopes, setCreateScopes] = useState<string[]>(['vehicles:read']);
  const [createExpiry, setCreateExpiry] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const d = await apiFetch<{ data: FleetApiKey[] }>('/api/api-keys');
      setKeys(d.data ?? []);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true); setCreateError(null);
    try {
      const r = await apiFetch<FleetApiKeyCreated>('/api/api-keys', { method: 'POST', body: JSON.stringify({ name: createName, scopes: createScopes, expires_in_days: createExpiry ? parseInt(createExpiry) : undefined }) });
      setNewKey(r); setShowCreate(false); setCreateName(''); setCreateScopes(['vehicles:read']); setCreateExpiry('');
      await load();
    } catch (e) { setCreateError(e instanceof Error ? e.message : 'Failed to create'); }
    finally { setCreating(false); }
  }

  async function handleRevoke(keyId: string, name: string) {
    if (!confirm(`Revoke "${name}"?`)) return;
    try {
      await apiFetch(`/api/api-keys/${keyId}`, { method: 'DELETE' });
      if (newKey?.id === keyId) setNewKey(null);
      await load();
    } catch (e) { alert(e instanceof Error ? e.message : 'Failed to revoke'); }
  }

  return (
    <div className="space-y-4">
      {newKey && (
        <div className="rounded-xl border border-emerald-800 bg-emerald-900/20 p-4">
          <p className="text-sm font-semibold text-emerald-300 mb-1">API key created — copy it now</p>
          <p className="text-xs text-muted-foreground mb-3">{newKey.warning}</p>
          <div className="flex items-center gap-2 rounded border border-border bg-card px-3 py-2">
            <code className="flex-1 text-xs text-foreground break-all">{newKey.key}</code>
            <button onClick={() => { navigator.clipboard.writeText(newKey.key); setCopied(true); setTimeout(() => setCopied(false), 2000); }}><Copy className="h-4 w-4 text-muted-foreground hover:text-foreground" /></button>
          </div>
          {copied && <p className="text-xs text-emerald-400 mt-1">Copied!</p>}
          <button onClick={() => setNewKey(null)} className="mt-2 text-xs text-muted-foreground hover:text-foreground">Dismiss</button>
        </div>
      )}
      {createError && <p className="text-sm text-rose-400">{createError}</p>}

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-foreground">API Keys</h3>
          <div className="flex items-center gap-2">
            <button onClick={load} className="text-muted-foreground hover:text-foreground"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-foreground hover:bg-emerald-500">
              <Plus className="h-3.5 w-3.5" /> New Key
            </button>
          </div>
        </div>

        {showCreate && (
          <form onSubmit={handleCreate} className="mb-5 space-y-3 rounded-lg border border-border bg-muted p-4">
            <div><label className={labelCls}>Key name</label><input type="text" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="e.g. Integration Key" className={inputCls} required /></div>
            <div>
              <label className={labelCls}>Scopes</label>
              <div className="flex flex-wrap gap-2">
                {FLEET_SCOPES.map(s => <label key={s} className="flex items-center gap-1.5 text-xs text-foreground cursor-pointer"><input type="checkbox" checked={createScopes.includes(s)} onChange={() => setCreateScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])} className="rounded border-border" />{s}</label>)}
              </div>
            </div>
            <div><label className={labelCls}>Expires in (days, optional)</label><input type="number" value={createExpiry} onChange={e => setCreateExpiry(e.target.value)} placeholder="Leave blank for no expiry" min="1" max="365" className="w-48 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500" /></div>
            <div className="flex gap-2">
              <button type="submit" disabled={creating || createScopes.length === 0} className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-foreground hover:bg-emerald-500 disabled:opacity-40">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}{creating ? 'Creating…' : 'Create Key'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="text-sm text-muted-foreground hover:text-foreground px-4 py-2">Cancel</button>
            </div>
          </form>
        )}

        {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 text-muted-foreground animate-spin" /></div>
          : error ? <p className="text-sm text-rose-400">{error}</p>
          : keys.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No API keys yet.</p>
          : <div className="divide-y divide-border">
            {keys.map(k => (
              <div key={k.id} className="flex items-start justify-between gap-4 py-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{k.name}</p>
                  <p className="text-xs font-mono text-muted-foreground mt-0.5">{k.key_prefix}…</p>
                  <div className="flex flex-wrap gap-1 mt-1">{k.scopes?.map(s => <span key={s} className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{s}</span>)}</div>
                  {k.expires_at && <p className="text-xs text-muted-foreground mt-0.5">Expires {new Date(k.expires_at).toLocaleDateString()}</p>}
                </div>
                <button onClick={() => handleRevoke(k.id, k.name)} className="text-muted-foreground hover:text-rose-400 transition-colors flex-shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}

// ─── Audit Log Tab ─────────────────────────────────────────────────────────────

interface FleetAuditEvent { id: string; actor_email?: string; action: string; action_category?: string; entity_type?: string; entity_name?: string; created_at: string; }

const FLEET_AUDIT_LIMIT = 25;

function FleetAuditTab() {
  const [events, setEvents] = useState<FleetAuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState('');
  const [actorEmail, setActorEmail] = useState('');
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ limit: String(FLEET_AUDIT_LIMIT), offset: String(offset) });
      if (category) params.set('category', category);
      if (actorEmail) params.set('actor_email', actorEmail);
      const d = await apiFetch<{ data: FleetAuditEvent[]; pagination: { total: number } }>(`/api/audit-log?${params}`);
      setEvents(d.data ?? []); setTotal(d.pagination?.total ?? 0);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
    finally { setLoading(false); }
  }, [category, actorEmail, offset]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / FLEET_AUDIT_LIMIT);
  const currentPage = Math.floor(offset / FLEET_AUDIT_LIMIT) + 1;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className={labelCls}>Category</label>
            <select value={category} onChange={e => { setCategory(e.target.value); setOffset(0); }} className="rounded border border-border bg-muted px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500">
              <option value="">All Categories</option>
              <option value="access">Access</option>
              <option value="data_update">Data Update</option>
              <option value="config">Config</option>
              <option value="security">Security</option>
            </select>
          </div>
          <div className="flex-1 min-w-48">
            <label className={labelCls}>Actor email</label>
            <input type="text" value={actorEmail} onChange={e => { setActorEmail(e.target.value); setOffset(0); }} placeholder="Filter by email…" className={inputCls} />
          </div>
          <button onClick={load} className="text-muted-foreground hover:text-foreground p-2"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Audit Log</h3>
          <span className="text-xs text-muted-foreground">{total} events</span>
        </div>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 text-muted-foreground animate-spin" /></div>
          : error ? <p className="text-sm text-rose-400 p-4">{error}</p>
          : events.length === 0 ? <p className="text-sm text-muted-foreground p-4 text-center">No audit events found.</p>
          : <div className="divide-y divide-border">
            {events.map(ev => (
              <div key={ev.id} className="flex items-start justify-between gap-4 px-5 py-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{ev.action}</span>
                    {ev.action_category && <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{ev.action_category}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {ev.actor_email && <span className="text-xs text-muted-foreground">{ev.actor_email}</span>}
                    {ev.entity_name && <span className="text-xs text-muted-foreground">→ {ev.entity_type}: {ev.entity_name}</span>}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground flex-shrink-0">{new Date(ev.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        }
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border">
            <span className="text-xs text-muted-foreground">Page {currentPage} of {totalPages}</span>
            <div className="flex gap-1">
              <button onClick={() => setOffset(Math.max(0, offset - FLEET_AUDIT_LIMIT))} disabled={offset === 0} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => setOffset(offset + FLEET_AUDIT_LIMIT)} disabled={offset + FLEET_AUDIT_LIMIT >= total} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
